import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { FrameworkAnalysis, FrameworkClauseStatus, FrameworkOverallStatus } from "../../types"

import { AlertTriangle, Check, ChevronDown, ChevronRight, MinusCircle } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { CitationText, type ExtractionItemLookup, type OpenSourceFn } from "./extraction-citation"

// ── Types ─────────────────────────────────────────────────────────────────────

type FrameworkTabProps = {
  frameworkAnalyses?: FrameworkAnalysis[]
  isLoading: boolean
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const OVERALL_STATUS_CONFIG: Record<
  FrameworkOverallStatus,
  { labelKey: string; badgeClass: string }
> = {
  compliant: {
    labelKey: "tender.bidReview.frameworkOverall.compliant",
    badgeClass: "border-success bg-success/10 text-success",
  },
  minor_deviations: {
    labelKey: "tender.bidReview.frameworkOverall.minor_deviations",
    badgeClass: "border-warning bg-warning/10 text-warning-foreground",
  },
  major_deviations: {
    labelKey: "tender.bidReview.frameworkOverall.major_deviations",
    badgeClass: "border-destructive bg-destructive/10 text-destructive",
  },
}

const CLAUSE_STATUS_CONFIG: Record<
  FrameworkClauseStatus,
  { icon: React.ElementType; iconClass: string }
> = {
  compliant: { icon: Check, iconClass: "text-success dark:text-success" },
  deviation: { icon: AlertTriangle, iconClass: "text-warning dark:text-warning-foreground" },
  not_addressed: { icon: MinusCircle, iconClass: "text-muted-foreground" },
}

const RISK_LABEL: Record<string, { labelKey: string; class: string }> = {
  lag: {
    labelKey: "tender.risk.lag",
    class: "border-success bg-success/10 text-success",
  },
  medel: {
    labelKey: "tender.risk.medel",
    class: "border-warning bg-warning/10 text-warning-foreground",
  },
  hog: {
    labelKey: "tender.risk.hog",
    class: "border-destructive bg-destructive/10 text-destructive",
  },
}

// ── Framework section ─────────────────────────────────────────────────────────

function FrameworkSection({
  framework,
  extractionLookup,
  onOpenSource,
}: {
  framework: FrameworkAnalysis
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const overallConf = OVERALL_STATUS_CONFIG[framework.overallStatus]
  const ChevronIcon = expanded ? ChevronDown : ChevronRight

  return (
    <div className="rounded-lg border">
      <div className="px-4 py-3">
        <Button
          aria-expanded={expanded}
          className="h-auto w-full justify-start gap-3 p-0 text-left font-normal"
          type="button"
          variant="ghost"
          onClick={() => setExpanded((value) => !value)}
        >
          <span className="-ml-1 flex size-7 shrink-0 items-center justify-center rounded-sm">
            <ChevronIcon className="text-muted-foreground size-5 shrink-0" />
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold">{framework.frameworkName}</span>
            <Badge
              className={cn("h-5 px-1.5 text-[11px]", overallConf.badgeClass)}
              variant="outline"
            >
              {t(overallConf.labelKey)}
            </Badge>
          </div>
        </Button>
        <div className="min-w-0 pl-7">
          {framework.summary && (
            <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
              {extractionLookup ? (
                <CitationText
                  lookup={extractionLookup}
                  text={framework.summary}
                  onOpenSource={onOpenSource}
                />
              ) : (
                framework.summary
              )}
            </p>
          )}
        </div>
      </div>

      {expanded && framework.clauseChecks.length > 0 && (
        <div className="border-t px-4 pb-3">
          <div className="flex flex-col gap-2 pt-3">
            {framework.clauseChecks.map((clause, i) => {
              const clauseConf = CLAUSE_STATUS_CONFIG[clause.status]
              const ClauseIcon = clauseConf.icon
              const riskConf = RISK_LABEL[clause.riskLevel]

              return (
                <div key={i} className="flex items-start gap-3 py-2">
                  <ClauseIcon className={cn("mt-0.5 size-4 shrink-0", clauseConf.iconClass)} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="bg-muted rounded px-1 py-0.5 font-mono text-[11px]">
                        {clause.clauseReference}
                      </code>
                      <span className="text-[12px] font-medium">
                        {extractionLookup ? (
                          <CitationText
                            lookup={extractionLookup}
                            text={clause.clauseDescription}
                            onOpenSource={onOpenSource}
                          />
                        ) : (
                          clause.clauseDescription
                        )}
                      </span>
                      {clause.status !== "compliant" && (
                        <Badge
                          className={cn("h-4 px-1.5 text-[10px]", riskConf.class)}
                          variant="outline"
                        >
                          {t("tender.bidReview.framework.riskBadge", {
                            level: t(riskConf.labelKey),
                          })}
                        </Badge>
                      )}
                    </div>
                    {clause.deviationDescription && (
                      <p className="text-warning-foreground dark:text-warning-foreground mt-1 text-[12px] leading-relaxed">
                        {extractionLookup ? (
                          <CitationText
                            lookup={extractionLookup}
                            text={clause.deviationDescription}
                            onOpenSource={onOpenSource}
                          />
                        ) : (
                          clause.deviationDescription
                        )}
                      </p>
                    )}
                    {clause.suggestedFix && (
                      <div className="bg-accent/10 text-accent-foreground dark:text-accent-foreground mt-1.5 rounded-md px-2 py-1.5 text-[12px] leading-relaxed">
                        <span className="font-medium">
                          {t("tender.bidReview.popover.actionPrefix")}
                        </span>
                        {extractionLookup ? (
                          <CitationText
                            lookup={extractionLookup}
                            text={clause.suggestedFix}
                            onOpenSource={onOpenSource}
                          />
                        ) : (
                          clause.suggestedFix
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {[0, 1].map((i) => (
        <Skeleton key={i} className="h-20 w-full" />
      ))}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function FrameworkTab({
  frameworkAnalyses,
  isLoading,
  extractionLookup,
  onOpenSource,
}: FrameworkTabProps) {
  const { t } = useTranslation()
  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (!frameworkAnalyses || frameworkAnalyses.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-[13px]">
        {t("tender.bidReview.framework.empty")}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {frameworkAnalyses.map((framework) => (
        <FrameworkSection
          key={framework.frameworkName}
          extractionLookup={extractionLookup}
          framework={framework}
          onOpenSource={onOpenSource}
        />
      ))}
    </div>
  )
}
